import { isFlagged, isOptimal, labStatus } from './labStatus';
import { THEME_ORDER } from './themes.config';
import { trendPct } from './trend';
import type {
  AwpBiomarker,
  AwpDashboard,
  AwpPanel,
  AwpPanelEntry,
  AwpValue,
  LabPoint,
  LabRow,
  ThemeSummary,
} from './types';

const OPERATOR_SYMBOL = { LT: '<', LTE: '≤', GT: '>', GTE: '≥' } as const;

/**
 * The value as text. Numbers come from `number`, not the lab's `raw`: `raw` is in the unit the lab
 * reported, while `number` and the bands are in the panel's `unit`.
 */
function displayOf(value: AwpValue): string {
  switch (value.__typename) {
    case 'NumericBiomarkerValue':
      return String(value.number);
    case 'ComparatorBiomarkerValue':
      return `${OPERATOR_SYMBOL[value.operator]}${value.number}`;
    case 'TiterBiomarkerValue':
      return `${value.numerator}:${value.denominator}`;
    case 'StringBiomarkerValue':
      return value.text;
    case 'NotPerformedBiomarkerValue':
    case 'NotReportableBiomarkerValue':
    case 'IndeterminateBiomarkerValue':
      return value.raw;
  }
}

function numberOf(value: AwpValue): number | null {
  return value.__typename === 'NumericBiomarkerValue' || value.__typename === 'ComparatorBiomarkerValue'
    ? value.number
    : null;
}

function toPoint(biomarker: AwpBiomarker, visitDate: string): LabPoint {
  const bands = biomarker.referenceBands ?? null;
  return {
    visitDate,
    collectedAt: biomarker.collectedAt,
    value: biomarker.value,
    display: displayOf(biomarker.value),
    number: numberOf(biomarker.value),
    bands,
    status: labStatus(biomarker.value, bands),
  };
}

/** The panel in `panels` for `date`, or undefined if it hasn't loaded (or isn't being requested at all). */
function panelOn(panels: AwpPanel[], date: string | undefined): AwpPanel | undefined {
  return date ? panels.find((panel) => panel.appointmentDate === date) : undefined;
}

/** The drawn biomarker for `key` at `date`, or null if that visit's panel hasn't loaded or didn't draw it. */
function drawnEntryAt(key: string, panels: AwpPanel[], date: string | undefined): AwpBiomarker | null {
  const entry = panelOn(panels, date)?.biomarkers.find((candidate) => candidate.loinc === key);
  return entry?.__typename === 'Biomarker' ? entry : null;
}

/**
 * `previous[i]` aligned to `visitDates[i + 1]`: null when that visit's panel hasn't loaded, or the
 * slot wasn't drawn there. Each point is judged against its own visit's bands (`toPoint` reads the
 * entry's own `referenceBands`), never the current visit's.
 */
function toPreviousPoints(key: string, panels: AwpPanel[], visitDates: string[] | undefined): (LabPoint | null)[] {
  if (!visitDates) return [];
  return visitDates.slice(1).map((date) => {
    const entry = drawnEntryAt(key, panels, date);
    return entry ? toPoint(entry, date) : null;
  });
}

/**
 * A slot's unit from whichever visit drew it, current preferred: the unit is fixed by the reference
 * document for the session, not by the visit — labs-api's published `Biomarker.unit` always comes from
 * the reference entry, never the specimen, and `referenceBands` are documented as "in the unit of the
 * value beside them or not given at all" (never a different one). So an undrawn current slot still
 * reads the right unit for its (older) `previous` points instead of going null just because nothing
 * was drawn this time.
 */
function unitOf(
  current: AwpBiomarker | null,
  key: string,
  panels: AwpPanel[],
  visitDates: string[] | undefined,
): string | null {
  if (current) return current.unit ?? null;
  for (const date of visitDates?.slice(1) ?? []) {
    const entry = drawnEntryAt(key, panels, date);
    if (entry) return entry.unit ?? null;
  }
  return null;
}

function toRow(entry: AwpPanelEntry, visitDate: string, panels: AwpPanel[], visitDates: string[] | undefined): LabRow {
  const drawnBiomarker = entry.__typename === 'Biomarker' ? entry : null;
  const current = drawnBiomarker ? toPoint(drawnBiomarker, visitDate) : null;
  const previous = toPreviousPoints(entry.loinc, panels, visitDates);
  return {
    key: entry.loinc,
    themeCode: entry.themeCode ?? null,
    name: entry.name,
    code: null,
    unit: unitOf(drawnBiomarker, entry.loinc, panels, visitDates),
    current,
    previous,
    trendPct: trendPct(current, previous[0] ?? null),
  };
}

/** Counts drawn rows only; a row with no theme belongs to no card. */
function summarize(rows: LabRow[]): ThemeSummary[] {
  return THEME_ORDER.map((themeCode) => {
    const summary: ThemeSummary = { themeCode, total: 0, flagged: 0, optimal: 0 };
    for (const row of rows) {
      if (row.themeCode !== themeCode || !row.current) continue;
      summary.total += 1;
      if (isFlagged(row.current.status)) summary.flagged += 1;
      if (isOptimal(row.current.status)) summary.optimal += 1;
    }
    return summary;
  });
}

/** The panel with the latest appointment date. Dates are `yyyy-MM-dd`, so they compare as strings. */
function newestPanel(panels: AwpPanel[]): AwpPanel | undefined {
  return panels.reduce<AwpPanel | undefined>(
    (newest, panel) => (!newest || panel.appointmentDate > newest.appointmentDate ? panel : newest),
    undefined,
  );
}

/**
 * The current panel is `visitDates[0]`'s, never just "whichever is newest": if the current visit's
 * panel is still pending or failed while an older one has loaded, the older panel must not be read
 * as current. Without `visitDates` (a bare list of panels, as some unit tests pass), newest is the
 * only ordering available.
 */
function currentPanelOf(panels: AwpPanel[], visitDates: string[] | undefined): AwpPanel | undefined {
  return visitDates ? panelOn(panels, visitDates[0]) : newestPanel(panels);
}

/**
 * The dashboard's view of the member's AWPs. `visitDates` (newest first: the current visit, then
 * `HISTORY_DEPTH` before it) says which visit is current and aligns `previous`/`history` to the
 * visits before it; omit it to read whichever panel is newest, with no history filled in.
 */
export function buildAwpDashboard(panels: AwpPanel[], visitDates?: string[]): AwpDashboard {
  const current = currentPanelOf(panels, visitDates);
  const rows = current
    ? current.biomarkers.map((entry) => toRow(entry, current.appointmentDate, panels, visitDates))
    : [];
  const themes = summarize(rows);

  return {
    currentVisit: current?.appointmentDate ?? null,
    rows,
    themes,
    outOfRangeTotal: themes.reduce((sum, theme) => sum + theme.flagged, 0),
  };
}
